import React, { useState } from "react";
import { CheckCircle2, Mail, MessageSquare, Send } from "lucide-react";
import api from "../api";
import SEO from "../components/SEO";

export default function ContactUs() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    order_no: "",
    comments: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Unable to send your message. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-20" data-testid="contact-page">
      <SEO
        title="Contact ProSource Labs — Customer Support"
        description="Contact ProSource Labs customer support. Questions about an order, product, lab testing certificate, or your account? Email support@prosourcelabs.org and we'll reply within 24 hours."
        path="/contact"
      />
      <div className="overline">CUSTOMER SUPPORT</div>
      <h1 className="font-heading text-4xl sm:text-5xl font-light leading-tight mt-4">
        Contact us.
      </h1>
      <p className="text-sm text-[#8A94A6] mt-4 max-w-2xl leading-relaxed">
        Questions about an order, a product, or your account? Send us a note and we'll reply
        within 24 hours.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-12">
        <div className="bg-[#0C0C12] p-8 space-y-3">
          <Mail className="text-[#00C3FF]" size={20} />
          <div className="overline" style={{ fontSize: 10 }}>EMAIL</div>
          <a href="mailto:support@prosourcelabs.org" data-testid="contact-email-link" className="font-mono-tech text-sm text-white hover:text-[#00C3FF] transition-colors block">support@prosourcelabs.org</a>
        </div>
        <div className="bg-[#0C0C12] p-8 space-y-3">
          <MessageSquare className="text-[#00C3FF]" size={20} />
          <div className="overline" style={{ fontSize: 10 }}>TEXT SUPPORT</div>
          <a href="sms:+19519873443" data-testid="contact-sms-link" className="font-mono-tech text-sm text-white hover:text-[#00C3FF] transition-colors block">(951) 987-3443</a>
          <div className="text-[10px] text-[#5C657A] font-mono-tech tracking-wider">TEXT ONLY · MON–FRI 9AM–6PM PT</div>
        </div>
        <div className="bg-[#0C0C12] p-8 space-y-3">
          <Send className="text-[#00C3FF]" size={20} />
          <div className="overline" style={{ fontSize: 10 }}>RESPONSE</div>
          <div className="font-mono-tech text-sm text-white">Within 24 hours</div>
        </div>
      </div>

      {sent ? (
        <div data-testid="contact-success" className="surface p-12 mt-12 text-center">
          <CheckCircle2 className="mx-auto text-[#00C3FF]" size={36} />
          <div className="overline mt-4">MESSAGE RECEIVED</div>
          <h2 className="font-heading text-3xl mt-2">Thank you.</h2>
          <p className="text-sm text-[#8A94A6] mt-3 max-w-md mx-auto">
            We'll reply at <span className="text-white">{form.email}</span> within 24 hours.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
          <div>
            <label className="overline block mb-2">Name</label>
            <input
              data-testid="contact-name-input"
              required
              className="input-tech"
              value={form.name}
              onChange={upd("name")}
              placeholder="Dr. Jane Doe"
            />
          </div>
          <div>
            <label className="overline block mb-2">Email</label>
            <input
              data-testid="contact-email-input"
              type="email"
              required
              className="input-tech"
              value={form.email}
              onChange={upd("email")}
              placeholder="researcher@lab.com"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="overline block mb-2">Order Number <span className="text-[#5C657A] normal-case tracking-normal">(optional)</span></label>
            <input
              data-testid="contact-order-input"
              className="input-tech"
              value={form.order_no}
              onChange={upd("order_no")}
              placeholder="PSL-ABCD1234"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="overline block mb-2">Comments</label>
            <textarea
              data-testid="contact-comments-input"
              required
              rows={6}
              className="input-tech"
              value={form.comments}
              onChange={upd("comments")}
              placeholder="How can we help?"
            />
          </div>

          {err && (
            <div
              data-testid="contact-error"
              className="sm:col-span-2 text-xs font-mono-tech text-red-400 border-l-2 border-red-400 pl-3"
            >
              {err}
            </div>
          )}

          <div className="sm:col-span-2">
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={busy}
              className="btn-primary"
            >
              {busy ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
